/**
 * Competition presets (SFB-1289).
 *
 * A preset (CompetitionPresetDTO) is a backend-defined template: account type,
 * duration, balances, ranking type, rules and milestones. The wizard offers
 * them as its first step; picking one pre-fills the later steps, which keep
 * their own validations and stay fully editable. The chosen key is also sent
 * as `presetKey` on creation so the backend can apply anything the wizard does
 * not model (milestones, minimum balance).
 */
import TournamentRepository from '@/repositories/tournament';
import { presetRuleToFormRule } from '@/views/Funding/Simulation/components/Wizard/tournamentRuleUtils';

/** Sentinel stored in `tournamentForm.presetKey` for the "start blank" card. */
export const BLANK_PRESET_KEY = '';

const PRESET_ICONS = {
  DEMO_SPRINT: 'fa-regular fa-bolt',
  VOLUME_LEAGUE: 'fa-regular fa-chart-column',
  CONSISTENCY_CHALLENGE: 'fa-regular fa-bullseye-arrow'
};

export function presetIcon(preset) {
  return PRESET_ICONS[preset?.key] || 'fa-regular fa-trophy';
}

export async function fetchCompetitionPresets() {
  const response = await TournamentRepository.getCompetitionPresets();
  return Array.isArray(response?.data) ? response.data : [];
}

function toDateInput(date) {
  return date.toISOString().substr(0, 10);
}

function addDays(dateInput, days) {
  const date = new Date(`${dateInput}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return toDateInput(date);
}

/**
 * Resets every configurable step (details, participants, rules, balance,
 * awards) while keeping the basic info the admin may already have typed
 * (name, community, schedule). Used by both "start blank" and preset picks so
 * switching cards never leaks values from a previous choice.
 */
function resetConfigurableSteps(store) {
  const keep = { ...store.state.tournamentForm.basicInfo };
  store.commit('resetTournamentForm');
  store.commit('updateTournamentBasicInfo', keep);
  store.commit('updateTournamentPlatformInfo', {
    platform: '',
    tournamentType: '',
    platformGroup: '',
    platformGroupId: null
  });
}

export function applyBlankToForm(store) {
  resetConfigurableSteps(store);
  store.commit('setTournamentPreset', { presetKey: BLANK_PRESET_KEY, presetSelected: true });
}

export function applyPresetToForm(store, preset) {
  resetConfigurableSteps(store);
  const form = store.state.tournamentForm;

  // Schedule: the backend derives endTime from durationDays only when endTime
  // is missing, but the basic-info step requires both dates, so pre-fill them
  // here. The admin can still change either date in the next step.
  if (preset.durationDays > 0) {
    const startDate = form.basicInfo.startDate || toDateInput(new Date());
    store.commit('updateTournamentBasicInfo', {
      ...form.basicInfo,
      startDate,
      endDate: addDays(startDate, preset.durationDays)
    });
  }

  if (preset.accountType) {
    store.commit('updateTournamentDetails', {
      ...form.details,
      tournamentType: preset.accountType
    });
  }

  if (preset.rankingType) {
    store.commit('updateTournamentParticipants', {
      ...form.participants,
      rankingType: preset.rankingType
    });
  }

  const balance = preset.initialBalance != null ? preset.initialBalance : preset.minimumBalance;
  if (balance != null) {
    store.commit('updateTournamentInitialBalance', String(balance));
  }

  if (Array.isArray(preset.rules) && preset.rules.length) {
    store.commit('updateTournamentRules', preset.rules.map(presetRuleToFormRule));
  }

  store.commit('setTournamentPreset', { presetKey: preset.key, presetSelected: true });
}
